// src/hooks/useTaskPermissions.ts
//
// FU-4 Step 8 — capability signals for the Tasks surface.
//
// `routes/tasks.ts` gates every route with `requireCapabilityMiddleware`
// ('tasks', read | write | delete) and nothing else, so these flags read
// exactly what the server enforces. `TasksPage` previously rendered create,
// edit and delete to every principal and let the server refuse.
//
// Follows the established per-domain pattern (`useContactCompanyPermissions`,
// `useProjectPermissions`, `useMessagePermissions`): one hook per domain so
// components never call `useHasCapability` with literals and never compare
// roles.
//
// ADR-0023 baseline — what an UNCONFIGURED membership resolves to:
//
//     role     read   write  delete
//     owner    true   true   true
//     admin    true   true   true
//     member   true   true   false
//     viewer   true   false  false
//
// A membership carrying an explicit `crm:` package resolves from its scope
// list instead, which is the reason these read the capability and not the
// role: an administrator who unticks "Create & edit" for one person must see
// that reflected in the UI, not only in a 403.
//
// FAILS CLOSED. `useHasCapability` returns false for a null context, and the
// context is null until boot reaches `ready`, so no control can flash enabled
// and then retract.
//
// THE SERVER REMAINS AUTHORITATIVE. These flags decide what is RENDERED; the
// route gates decide what is PERMITTED.

import { useHasCapability } from '../services/centralContext';

export interface TaskPermissions {
  canViewTasks: boolean;
  /** Create, edit, reassign, change status. All map to `write`. */
  canWriteTasks: boolean;
  canDeleteTasks: boolean;
}

export function useTaskPermissions(): TaskPermissions {
  return {
    canViewTasks: useHasCapability('tasks', 'read'),
    canWriteTasks: useHasCapability('tasks', 'write'),
    canDeleteTasks: useHasCapability('tasks', 'delete'),
  };
}
